'use client'

/**
 * ToastNotifications — ui-ux-pro-max
 *
 * Transient non-critical feedback:
 * - Auto-dismiss 3–5s by severity (duration: 0 = persistent)
 * - aria-live polite; role=alert for errors (no focus steal)
 * - Optional action buttons (Undo / Retry)
 * - Stack top-right below chrome — never covers bottom-right primary CTAs
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
} from 'react'
import styles from './feedback.module.css'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastOptions {
  /**
   * Auto-dismiss delay in ms.
   * Defaults: success 3.5s · info 4s · warning/error 5s (UX 3–5s band).
   * Pass `0` to keep until the user dismisses (or taps an action).
   */
  duration?: number
  /** Primary recovery / undo control rendered on the toast */
  action?: ToastAction
  /** Pause the dismiss timer while hovered or focused (default true) */
  pauseOnHover?: boolean
}

export interface Toast {
  id: string
  type: ToastType
  message: string
  action?: ToastAction
  duration: number
  pauseOnHover: boolean
}

interface ToastContextValue {
  toasts: Toast[]
  addToast: (type: ToastType, message: string, options?: ToastOptions) => string
  removeToast: (id: string) => void
}

/** UX guideline: auto-dismiss 3–5s; errors get the upper bound for readability */
const DEFAULT_DURATION_MS: Record<ToastType, number> = {
  success: 3500,
  info: 4000,
  warning: 5000,
  error: 5000,
}

const MAX_VISIBLE = 5

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastNotificationsProvider')
  }
  return context
}

interface ToastNotificationsProviderProps {
  children: ReactNode
}

const TYPE_CLASS: Record<ToastType, string> = {
  success: styles.toastSuccess ?? 'toast-success',
  error: styles.toastError ?? 'toast-error',
  warning: styles.toastWarning ?? 'toast-warning',
  info: styles.toastInfo ?? 'toast-info',
}

interface TimerEntry {
  timeoutId: number | null
  remaining: number
  startedAt: number
}

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return Math.random().toString(36).slice(2, 11)
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
      <path
        d="M3.5 3.5l7 7M10.5 3.5l-7 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function ToastNotificationsProvider({ children }: ToastNotificationsProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timersRef = useRef<Map<string, TimerEntry>>(new Map())

  const clearTimer = useCallback((id: string) => {
    const entry = timersRef.current.get(id)
    if (entry?.timeoutId != null) {
      window.clearTimeout(entry.timeoutId)
    }
    timersRef.current.delete(id)
  }, [])

  const removeToast = useCallback(
    (id: string) => {
      clearTimer(id)
      setToasts((prev) => prev.filter((t) => t.id !== id))
    },
    [clearTimer]
  )

  const scheduleDismiss = useCallback(
    (id: string, duration: number) => {
      if (duration <= 0) return
      const existing = timersRef.current.get(id)
      if (existing?.timeoutId != null) {
        window.clearTimeout(existing.timeoutId)
      }
      const startedAt = Date.now()
      const timeoutId = window.setTimeout(() => {
        removeToast(id)
      }, duration)
      timersRef.current.set(id, { timeoutId, remaining: duration, startedAt })
    },
    [removeToast]
  )

  const pauseTimer = useCallback((id: string) => {
    const entry = timersRef.current.get(id)
    if (!entry || entry.timeoutId == null) return
    window.clearTimeout(entry.timeoutId)
    const elapsed = Date.now() - entry.startedAt
    const remaining = Math.max(0, entry.remaining - elapsed)
    timersRef.current.set(id, { timeoutId: null, remaining, startedAt: Date.now() })
  }, [])

  const resumeTimer = useCallback(
    (id: string) => {
      const entry = timersRef.current.get(id)
      if (!entry || entry.timeoutId != null) return
      if (entry.remaining <= 0) {
        removeToast(id)
        return
      }
      scheduleDismiss(id, entry.remaining)
    },
    [removeToast, scheduleDismiss]
  )

  const addToast = useCallback(
    (type: ToastType, message: string, options?: ToastOptions) => {
      const id = newId()
      const duration =
        typeof options?.duration === 'number'
          ? Math.max(0, options.duration)
          : DEFAULT_DURATION_MS[type]
      const pauseOnHover = options?.pauseOnHover !== false
      // When an action is present and duration was not set, stay slightly longer
      // so users can hit Undo/Retry — still within / near the 3–5s band unless 0.
      const resolvedDuration =
        options?.duration === undefined && options?.action
          ? Math.min(6000, Math.max(duration, 5000))
          : duration

      const toast: Toast = {
        id,
        type,
        message,
        action: options?.action,
        duration: resolvedDuration,
        pauseOnHover,
      }

      setToasts((prev) => {
        const next = [...prev, toast]
        // Cap stack; drop oldest first
        if (next.length > MAX_VISIBLE) {
          const dropped = next.slice(0, next.length - MAX_VISIBLE)
          dropped.forEach((t) => clearTimer(t.id))
          return next.slice(-MAX_VISIBLE)
        }
        return next
      })

      scheduleDismiss(id, resolvedDuration)
      return id
    },
    [clearTimer, scheduleDismiss]
  )

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((entry) => {
        if (entry.timeoutId != null) window.clearTimeout(entry.timeoutId)
      })
      timersRef.current.clear()
    }
  }, [])

  const handleAction = useCallback(
    (toast: Toast) => {
      try {
        toast.action?.onClick()
      } finally {
        removeToast(toast.id)
      }
    },
    [removeToast]
  )

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      {/*
        Live region: polite for status; individual error toasts use role="alert".
        Never auto-focus — toast-accessibility (WCAG).
        Position: top-right below app chrome; clear of bottom-right primary CTAs / ScrollTop.
      */}
      <div
        className={styles.toastStack}
        aria-live="polite"
        aria-relevant="additions text"
        aria-atomic="false"
      >
        {toasts.map((toast) => {
          const isError = toast.type === 'error'
          return (
            <div
              key={toast.id}
              className={`${styles.toast} ${TYPE_CLASS[toast.type]}`}
              role={isError ? 'alert' : 'status'}
              aria-live={isError ? 'assertive' : undefined}
              onMouseEnter={() => toast.pauseOnHover && pauseTimer(toast.id)}
              onMouseLeave={() => toast.pauseOnHover && resumeTimer(toast.id)}
              onFocus={() => toast.pauseOnHover && pauseTimer(toast.id)}
              onBlur={() => toast.pauseOnHover && resumeTimer(toast.id)}
            >
              <div className={styles.toastBody}>
                <span className={styles.toastMessage}>{toast.message}</span>
                {toast.action ? (
                  <div className={styles.toastActions}>
                    <button
                      type="button"
                      className={styles.toastAction}
                      onClick={() => handleAction(toast)}
                    >
                      {toast.action.label}
                    </button>
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                className={styles.toastDismiss}
                onClick={() => removeToast(toast.id)}
                aria-label="Dismiss notification"
              >
                <CloseIcon />
              </button>
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}

export default ToastNotificationsProvider
